import React, { useCallback, useEffect, useState } from "react";
import { Badge, Popover, Tooltip, Spin } from "antd";
import { useSelector } from "react-redux";

import * as watchersApi from "../../../../core/data/redux/services/watchersApi";
import { selectActiveCompany } from "../../../../core/data/redux/projects/selectors";
import UserAvatar from "../../components/UserAvatar";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";
import type { Watcher } from "../../../../core/data/redux/projects/types";

interface WatcherMenuProps {
  issueId: number;
  currentUserId: number | null;
  initialWatchers?: Watcher[];
}

const WatcherMenu: React.FC<WatcherMenuProps> = ({
  issueId,
  currentUserId,
  initialWatchers = [],
}) => {
  const activeCompany = useSelector(selectActiveCompany);
  const [watchers, setWatchers] = useState<Watcher[]>(initialWatchers);
  const [loading, setLoading] = useState(false);

  const isWatching = watchers.some((w) => w.user_id === currentUserId);

  const reload = useCallback(async () => {
    if (!activeCompany?.company_id) return;
    setLoading(true);
    try {
      const data = await watchersApi.listForIssue(
        activeCompany.company_id,
        activeCompany.company_type,
        issueId
      );
      setWatchers(data?.watchers || []);
    } catch {
      // silently handled — leave watcher list empty
    } finally {
      setLoading(false);
    }
  }, [activeCompany, issueId]);

  useEffect(() => {
    if (initialWatchers.length === 0) reload();
  }, [reload, initialWatchers.length]);

  const toggleWatch = async () => {
    try {
      if (isWatching) {
        await watchersApi.removeWatcher(
          activeCompany.company_id,
          activeCompany.company_type,
          issueId
        );
        notify.success("Stopped watching");
      } else {
        await watchersApi.addWatcher(
          activeCompany.company_id,
          activeCompany.company_type,
          issueId
        );
        notify.success("Now watching");
      }
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to update watchers"));
    }
  };

  const watcherList = (
    <div style={{ maxWidth: 220, maxHeight: 200, overflow: "auto" }}>
      {loading ? (
        <div className="text-center py-2"><Spin size="small" /></div>
      ) : watchers.length === 0 ? (
        <p className="text-muted small mb-0">No watchers</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {watchers.map((w) => (
            <li key={w.id} className="d-flex align-items-center gap-2 py-1">
              <UserAvatar
                name={w.user_snapshot?.name}
                img={w.user_snapshot?.img}
                size={20}
                fontSize={9}
              />
              <span className="small">{w.user_snapshot?.name || `User #${w.user_id}`}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="d-inline-flex align-items-center gap-1">
      <Tooltip title={isWatching ? "Stop watching" : "Watch this issue"}>
        <button
          type="button"
          className="btn btn-sm"
          style={{
            borderRadius: 6,
            padding: "2px 8px",
            background: isWatching ? "#3b82f6" : "#fff",
            color: isWatching ? "#fff" : "#475569",
            border: `1px solid ${isWatching ? "#3b82f6" : "#cbd5e1"}`,
          }}
          onClick={toggleWatch}
        >
          <i className={`ti ti-eye${isWatching ? "" : "-off"}`} style={{ fontSize: 16 }} />
        </button>
      </Tooltip>
      <Popover
        content={watcherList}
        title={`${watchers.length} watcher${watchers.length !== 1 ? "s" : ""}`}
        trigger="click"
        placement="bottomRight"
      >
        <Badge
          count={watchers.length}
          style={{ backgroundColor: "#3b82f6", cursor: "pointer" }}
          size="small"
        />
      </Popover>
    </div>
  );
};

export default WatcherMenu;
